/**
 * Inbox Manager API Client
 *
 * Wrapper functions for all inbox/message/interaction endpoints.
 * Uses the shared KodeonApiClient for authenticated HTTP requests.
 *
 * Backend endpoints verified in routes_generated.go (Task 2569 §2).
 */

import { api } from '@/lib/api'
import { getExecutionsByFacet } from '@/features/automations/api'
import type {
  Interaction,
  InteractionListResponse,

  Message,
  MessageAttachment,
  InboxSourceListResponse,
  ReplyRequest,
  SendRequest,
  CaseNotesRequest,
  ClassificationRequest,
  BatchReadRequest,
  BatchArchiveRequest,
  InboxFilters,
  InboxSortState,
  AutomationListResponse,
  AutomationExecuteResponse,
  FindCustomerParams,
  FindCustomerResponse,
} from './types'

// ── API Paths ────────────────────────────────────────────────────────────────

const PATHS = {
  // Canonical inbox / interactions endpoints.
  // (See services/communications/backend/internal/handlers/inbox_handler.go
  // for the design rationale that introduced these.)
  interactions: '/v1/interactions',
  interactionThread: (id: string) => `/v1/interactions/${id}/thread`,
  // Single-message detail and write endpoints
  message: (id: string) => `/v1/messages/${id}`,
  reply: '/v1/messages/reply',
  send: '/v1/messages/send',
  resend: '/v1/messages/resend',
  interaction: (id: string) => `/v1/interactions/${id}`,
  interactionAssets: (id: string) => `/v1/interactions/${id}/assets`,
  interactionRead: (id: string) => `/v1/interactions/${id}/read`,
  interactionProcessingStatus: (id: string) => `/v1/interactions/${id}/processing-status`,
  interactionBatchRead: '/v1/interactions/batch-read',
  interactionBatchArchive: '/v1/interactions/batch-archive',
  interactionCaseNotes: (id: string) => `/v1/interactions/${id}/case-notes`,
  interactionClassification: (id: string) => `/v1/interactions/${id}/classification`,
  interactionClassify: (id: string) => `/v1/interactions/${id}/classify`,
  inboxes: '/v1/inboxes',
  automations: '/v1/automations',
  automationExecute: (id: string) => `/v1/automations/${id}/execute`,
  findCustomer: '/v1/agents/customers/find',
} as const

// ── List Interactions ────────────────────────────────────────────────────────

export interface ListMessagesParams {
  filters?: InboxFilters
  sort?: InboxSortState
  limit?: number
  cursor?: string
  /**
   * Response projection. Defaults to `full` for the Stream inbox so the
   * UI can render the v3 Classification block (sentiment, urgency,
   * required_action, tags, etc.) persisted by the content_classifier
   * (Task 3508). Pass `summary` for callers that only need the compact
   * row projection (no Classification details).
   *
   * Note: the backend caps `limit` at 100 when `view=full` (200 for
   * summary) and emits a Warning header if exceeded.
   */
  view?: 'summary' | 'full'
  /**
   * When true, the backend performs a single batch Firestore query to
   * fetch the most-recent outbound reply for each inbound interaction on
   * the page and annotates each row with `reply_preview` and `replied_at`.
   * One extra backend query per list request; no additional HTTP calls.
   */
  includeReplyPreview?: boolean
}

/**
 * List interactions across all inboxes with optional filtering and cursor
 * pagination. Calls the canonical GET /v1/interactions endpoint.
 *
 * Notable behaviour:
 *   - Absent `direction` filter returns ALL directions (no default).
 *   - `cursor` maps to the `starting_after` query param and is the id of
 *     the last item in the previous page.
 *   - Defaults to `view=full` so the operator inbox receives the structured
 *     v3 Classification block (Task 3508 follow-up).
 */
export async function listInteractions(
  params: ListMessagesParams = {}
): Promise<InteractionListResponse> {
  const queryParams: Record<string, string | number | boolean> = {}

  // Response projection — default to `full` so the operator inbox gets the
  // structured Classification block. Callers can opt back to `summary` if
  // they only need row metadata.
  const view = params.view ?? 'full'
  queryParams.view = view

  // Pagination — clamp `view=full` to its 100-item maximum to avoid the
  // Warning-header round trip.
  if (params.limit) {
    queryParams.limit = view === 'full' ? Math.min(params.limit, 100) : params.limit
  }
  if (params.cursor) queryParams.starting_after = params.cursor

  // Filters — map to query params the backend supports
  if (params.filters?.q) queryParams.q = params.filters.q
  // Field-scoped sender lookups (Task 4053f): exact, normalized matches on
  // the backend's indexed `search_keys` map — whole-collection, windowless.
  if (params.filters?.from_email) queryParams.from_email = params.filters.from_email
  if (params.filters?.from_name) queryParams.from_name = params.filters.from_name
  if (params.filters?.received_from) queryParams.received_from = params.filters.received_from
  if (params.filters?.received_to) queryParams.received_to = params.filters.received_to
  if (params.filters?.attachment !== undefined) queryParams.has_attachment = params.filters.attachment

  // Multi-value filters serialized as comma-separated strings
  if (params.filters?.processing_status?.length) queryParams.processing_status = params.filters.processing_status.join(',')
  if (params.filters?.direction) queryParams.direction = params.filters.direction
  if (params.filters?.type?.length) queryParams.type = params.filters.type.join(',')
  if (params.filters?.classification?.length) queryParams.classification = params.filters.classification.join(',')
  if (params.filters?.inbox_id?.length) queryParams.inbox_id = params.filters.inbox_id.join(',')

  // The Sent view shows support replies (operator-typed or agent-sent). Exclude
  // system/Mailgun transactional emails (is_automated=true) such as welcome
  // emails and data-export notifications — these are created by send_processor
  // with IsAutomated=true, whereas all support replies flow through reply_service
  // which leaves IsAutomated unset (false by default).
  if (params.filters?.direction === 'outbound') {
    queryParams.is_automated = false
  }

  // Sort — only sort_direction is honoured by the backend today; the
  // sort field is fixed to received_at.
  if (params.sort?.direction) queryParams.sort_direction = params.sort.direction

  // Reply preview annotation — triggers one extra batch Firestore query on
  // the backend per list request; always enabled for the operator inbox.
  if (params.includeReplyPreview) queryParams.include_reply_preview = true

  const response = await api.get<InteractionListResponse>(PATHS.interactions, queryParams)

  // Normalise rows so the inbox renders regardless of which backend
  // revision is live. Earlier revisions of GET /v1/interactions?view=full
  // emitted the raw `*interactions.Interaction` domain object with
  // `subject`/`from`/`received_at` nested under `email.*` (and the
  // review title baked into `plain_text_body`). The current revision
  // (a86f32c4 onwards) returns those fields flat at the top level via
  // the `InteractionFull` projection. Operators briefly saw an inbox of
  // blank rows in between deploys — this defensively copes with both
  // shapes so the UI never blanks again if the contract drifts.
  return {
    ...response,
    data: (response?.data ?? []).map(normaliseInteraction),
  }
}

// ── Response normalisation ───────────────────────────────────────────────────

/**
 * Coerces a single inbox row to the canonical flat `Interaction` shape.
 *
 * Handles two on-the-wire shapes:
 *
 *   1. The current `InteractionFull` / `InteractionSummary` shape with
 *      flat `subject` / `from` / `received_at` / `customer_email` /
 *      `from_name` / `to` / `preview` / `has_attachment` / `app_id` /
 *      `app_name`. Returned as-is (with defensive fallbacks for null).
 *
 *   2. The raw `*interactions.Interaction` shape an older backend revision
 *      emitted for `view=full`, where `subject` / `from` lived under
 *      `email.{subject,from}`, the review title was the first line of
 *      `plain_text_body`, and `has_attachment` had to be derived from
 *      `attachments.length`. We mirror the Go `buildInteractionSummary`
 *      derivation so rows render identically.
 *
 * Anything we cannot derive falls back to a safe empty value (empty
 * string, undefined, or `0`) — never `undefined` for fields the UI
 * destructures (e.g. `subject`, `received_at`) so the render path stays
 * exception-free.
 */
function normaliseInteraction(raw: unknown): Interaction {
  const row = (raw ?? {}) as Record<string, unknown> & Partial<Interaction>
  const email = (row as { email?: Record<string, unknown> }).email
  const review = (row as { review?: Record<string, unknown> }).review
  const caseNotes = (row as { case_notes?: Record<string, unknown> }).case_notes
  const attachments = (row as { attachments?: unknown[] }).attachments
  const plainTextBody = (row as { plain_text_body?: string }).plain_text_body
  const created = (row as { created?: number }).created

  const isReview = row.type === 'review'

  // ── Subject ─────────────────────────────────────────────────────────────
  let subject = pickString(row.subject)
  if (!subject) subject = pickString(email?.subject)
  if (!subject && isReview) {
    const { title } = splitReviewBody(plainTextBody)
    if (title) subject = title
    else {
      const appName = pickString(row.app_name) || pickString(caseNotes?.app_name)
      if (appName) subject = `Review of ${appName}`
    }
  }

  // ── From ────────────────────────────────────────────────────────────────
  let from = pickString(row.from)
  if (!from) from = pickString(email?.from)
  if (!from && isReview) from = pickString(caseNotes?.reviewer_nickname)

  // ── To / counterparty ───────────────────────────────────────────────────
  const to =
    (row.to as string[] | undefined) ??
    (email?.to as string[] | undefined) ??
    undefined

  const customer_email =
    pickString(row.customer_email) ||
    pickString(email?.customer_email) ||
    undefined

  // ── Received at ─────────────────────────────────────────────────────────
  let received_at = pickNumber(row.received_at)
  if (!received_at) received_at = pickNumber(email?.date)
  if (!received_at) received_at = pickNumber(created)

  // ── Preview ─────────────────────────────────────────────────────────────
  let preview = pickString(row.preview) || undefined
  if (!preview && plainTextBody) {
    const body = isReview ? splitReviewBody(plainTextBody).body : plainTextBody
    preview = body.slice(0, 200)
  }

  // ── has_attachment ──────────────────────────────────────────────────────
  const has_attachment =
    typeof row.has_attachment === 'boolean'
      ? row.has_attachment
      : Array.isArray(attachments) && attachments.length > 0

  // ── from_name ───────────────────────────────────────────────────────────
  let from_name = pickString(row.from_name) || undefined
  if (!from_name && from) {
    // "Display Name <foo@bar.com>" → "Display Name". Defensive only — the
    // backend normally splits this for us; we just want the inbox row to
    // show a friendly label if the raw header leaks through.
    const m = from.match(/^\s*"?([^"<]+?)"?\s*<[^>]+>\s*$/)
    if (m) from_name = m[1].trim() || undefined
  }

  // ── App context (review rows) ───────────────────────────────────────────
  const app_id =
    pickString(row.app_id) || pickString(caseNotes?.app_id) || undefined
  const app_name =
    pickString(row.app_name) || pickString(caseNotes?.app_name) || undefined

  // ── Review summary ──────────────────────────────────────────────────────
  let reviewSummary = (row as { review?: Interaction['review'] }).review
  if (!reviewSummary && review && typeof review === 'object') {
    reviewSummary = {
      rating: pickNumber((review as Record<string, unknown>).rating) || 0,
      territory: pickString((review as Record<string, unknown>).territory) || undefined,
      app_version: pickString((review as Record<string, unknown>).app_version) || undefined,
      asc_review_id: pickString((review as Record<string, unknown>).asc_review_id) || undefined,
    }
  }

  return {
    ...(row as Interaction),
    subject: subject ?? '',
    from: from ?? '',
    from_name,
    to,
    customer_email,
    received_at,
    preview,
    has_attachment,
    app_id,
    app_name,
    review: reviewSummary,
  }
}

function pickString(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function pickNumber(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0
}

/**
 * Reviews are persisted in the form "<title>\n\n<body>"; mirrors
 * `splitReviewTitleAndBody` in the Go backend so the title/body fallback
 * stays in lockstep with `buildInteractionSummary`.
 */
function splitReviewBody(combined: string | undefined): { title: string; body: string } {
  if (!combined) return { title: '', body: '' }
  const sep = '\n\n'
  const idx = combined.indexOf(sep)
  if (idx <= 0) return { title: '', body: combined }
  return {
    title: combined.slice(0, idx).trim(),
    body: combined.slice(idx + sep.length).trim(),
  }
}

// ── Get Message Detail ───────────────────────────────────────────────────────

/**
 * Get a single message's full content.
 * GET /v1/messages/{id}
 */
export async function getMessage(id: string): Promise<Message> {
  return api.get<Message>(PATHS.message(id))
}

// ── Get Interaction Detail ────────────────────────────────────────────────────

/**
 * Get interaction (thread) detail.
 * GET /v1/interactions/{id}
 */
export async function getInteraction(id: string): Promise<Interaction> {
  const raw = await api.get<Interaction>(PATHS.interaction(id))
  // GET /v1/interactions/{id} returns the raw domain object — coerce to
  // the canonical flat shape the UI expects.
  return normaliseInteraction(raw)
}

/**
 * Get interaction assets (attachments).
 * GET /v1/interactions/{id}/assets
 */
export async function getInteractionAssets(id: string): Promise<{ data: MessageAttachment[] }> {
  return api.get(PATHS.interactionAssets(id))
}

/**
 * Enrich message attachments with full asset metadata.
 *
 * The backend returns attachments on messages as plain asset-ID strings
 * (e.g. `["ast_abc"]`) rather than rich objects. Given the asset records from
 * GET /v1/interactions/{id}/assets, this replaces each raw ID with the
 * corresponding `MessageAttachment` (filename, content_type, size, url) so the
 * shared AttachmentChip / AttachmentPreviewModal components can render and
 * preview them. Unknown IDs degrade to a minimal placeholder; already-rich
 * objects are passed through unchanged.
 *
 * Shared by the inbox detail/history loaders (store) and the attention-queue
 * `InteractionEmailRenderer` (via fetchInteractionDetail) so attachments are
 * surfaced consistently wherever the original message is shown.
 */
export function enrichMessageAttachments(
  messages: Message[],
  rawAssets: MessageAttachment[] | undefined,
): Message[] {
  const assetMap = new Map<string, MessageAttachment>()
  for (const rawAsset of rawAssets ?? []) {
    // The assets endpoint returns files.File objects (Go backend) with fields:
    // id, filename, type (mime), size, url, gcs_path, etc.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const a = rawAsset as any
    const att: MessageAttachment = {
      id: a.id ?? '',
      filename: a.filename ?? 'attachment',
      content_type: a.type ?? a.content_type ?? 'application/octet-stream',
      size: Number(a.size ?? 0),
      url: a.url ?? undefined,
    }
    assetMap.set(att.id, att)
  }

  return messages.map((msg) => {
    const rawAttachments = msg.attachments as unknown
    if (!Array.isArray(rawAttachments) || rawAttachments.length === 0) return msg

    const enriched: MessageAttachment[] = rawAttachments.map((item: unknown) => {
      if (typeof item === 'string') {
        // Raw asset ID — look up the full record from the assets endpoint.
        return (
          assetMap.get(item) ?? {
            id: item,
            filename: 'attachment',
            content_type: 'application/octet-stream',
            size: 0,
          }
        )
      }
      // Already a rich object (defensive — current backend sends ID strings).
      return item as MessageAttachment
    })

    return { ...msg, attachments: enriched }
  })
}

// ── Reply & Send ─────────────────────────────────────────────────────────────

/**
 * Reply to a message thread.
 * POST /v1/messages/reply
 */
export async function replyToMessage(data: ReplyRequest): Promise<Message> {
  return api.post<Message>(PATHS.reply, data)
}

/**
 * Send a new outgoing message.
 * POST /v1/messages/send
 */
export async function sendMessage(data: SendRequest): Promise<Message> {
  return api.post<Message>(PATHS.send, data)
}

/**
 * Re-send a previously sent message.
 * POST /v1/messages/resend
 */
export async function resendMessage(messageId: string): Promise<Message> {
  return api.post<Message>(PATHS.resend, { message_id: messageId })
}

// ── Read Status ───────────────────────────────────────────────────────────────

/**
 * Mark an interaction as read.
 * PUT /v1/interactions/{id}/read
 */
export async function markRead(interactionId: string): Promise<void> {
  await api.put(PATHS.interactionRead(interactionId), {})
}

/**
 * Mark an interaction as unread.
 * DELETE /v1/interactions/{id}/read
 */
export async function markUnread(interactionId: string): Promise<void> {
  await api.delete(PATHS.interactionRead(interactionId))
}

/**
 * Bulk mark interactions as read.
 * POST /v1/interactions/batch-read
 */
export async function batchMarkRead(data: BatchReadRequest): Promise<void> {
  await api.post(PATHS.interactionBatchRead, data)
}

/**
 * Bulk archive interactions.
 * POST /v1/interactions/batch-archive
 */
export async function batchArchiveInteractions(data: BatchArchiveRequest): Promise<void> {
  await api.post(PATHS.interactionBatchArchive, data)
}

// ── Case Notes ────────────────────────────────────────────────────────────────

/**
 * Update case notes for an interaction.
 * PUT /v1/interactions/{id}/case-notes
 */
export async function updateCaseNotes(
  interactionId: string,
  data: CaseNotesRequest
): Promise<void> {
  await api.put(PATHS.interactionCaseNotes(interactionId), data)
}

// ── Processing Status ─────────────────────────────────────────────────────────

/**
 * Update the processing status of an interaction.
 * PUT /v1/interactions/{id}/processing-status
 */
export async function updateProcessingStatus(
  interactionId: string,
  processingStatus: string
): Promise<{ id: string; processing_status: string }> {
  return api.put(PATHS.interactionProcessingStatus(interactionId), {
    processing_status: processingStatus,
  })
}

// ── Classification ────────────────────────────────────────────────────────────

/**
 * Set classification on an interaction.
 * PUT /v1/interactions/{id}/classification
 */
export async function setClassification(
  interactionId: string,
  data: ClassificationRequest
): Promise<Interaction> {
  return api.put<Interaction>(PATHS.interactionClassification(interactionId), data)
}

/**
 * Auto-classify an interaction via AI.
 * POST /v1/interactions/{id}/classify
 */
export async function autoClassify(interactionId: string): Promise<Interaction> {
  return api.post<Interaction>(PATHS.interactionClassify(interactionId), {})
}

// ── Draft ─────────────────────────────────────────────────────────────────────
//
// `saveDraft()` was removed: the communications service does not implement
// `POST /v1/interactions/{id}/draft` (despite that path appearing in the
// OpenAPI spec as an AI-drafting trigger), so calls 404'd silently. Re-add
// an operator-draft endpoint on the backend before reintroducing the
// frontend wrapper and the Reply composer's Save Draft button.

// ── Inboxes ───────────────────────────────────────────────────────────────────

/**
 * List configured source inboxes.
 * GET /v1/inboxes — used to populate inbox_id facet filter options.
 */
export async function listInboxes(): Promise<InboxSourceListResponse> {
  return api.get<InboxSourceListResponse>(PATHS.inboxes)
}

// ── Automations ───────────────────────────────────────────────────────────────

export interface ListAutomationsParams {
  triggerEventType?: string
  enabled?: boolean
  originService?: string
}

/**
 * List automations matching the given filters.
 * GET /v1/automations
 *
 * Used to populate the "send to automation" dropdown in the inbox inspector
 * with workflows that accept manually-dispatched interactions (typically
 * those triggered by `interaction.received`).
 */
export async function listAutomations(
  params: ListAutomationsParams = {}
): Promise<AutomationListResponse> {
  const queryParams: Record<string, string | boolean> = {}
  if (params.triggerEventType) queryParams.trigger_event_type = params.triggerEventType
  if (params.enabled !== undefined) queryParams.enabled = params.enabled
  if (params.originService) queryParams.origin_service = params.originService

  return api.get<AutomationListResponse>(PATHS.automations, queryParams)
}

export interface ExecuteAutomationRequest {
  interaction_id: string
  dispatched_by?: string
}

/**
 * Dispatch an automation execution for a specific interaction.
 * POST /v1/automations/{id}/execute
 *
 * Publishes an `automation.dispatch_requested` event; the operations
 * consumer then runs ExecuteAutomation() against this single automation.
 * Returns immediately with `{status: "dispatched", event_id}` (202 Accepted).
 */
export async function executeAutomation(
  automationId: string,
  data: ExecuteAutomationRequest
): Promise<AutomationExecuteResponse> {
  return api.post<AutomationExecuteResponse>(
    PATHS.automationExecute(automationId),
    data
  )
}

// ── Agent Session Lookup ─────────────────────────────────────────────────────

/**
 * The agent session associated with an interaction — both the session id
 * and the agent id that produced it.  The agent id is required by the
 * per-agent `/v1/agents/{agentId}/sessions/{sessionId}/history` endpoint
 * used to render the Customer tab's find_customer timeline.
 */
export interface InteractionAgentSession {
  sessionId: string
  agentId: string
}

/**
 * Find the agent session associated with a given interaction.
 *
 * The operations service stores `interaction_id` as a facet on every
 * AutomationExecution spawned from that interaction. When an
 * `invoke_agent` step runs, it writes both `session_id` and `agent_id`
 * into the step output map (see
 * `services/operations/backend/internal/core/services/step_handlers/invoke_agent_step.go`).
 * This function delegates to the shared ``getExecutionsByFacet`` client in
 * ``features/automations/api`` (Task 4054c) and extracts the first matched
 * pair across step outputs.
 *
 * Returns `null` when no execution has been run for this interaction yet
 * (e.g. the agent has not investigated the case, or the case pre-dates
 * `agent.tool.called` instrumentation).
 *
 * Tasks 3119, 4054c.
 */
export async function getInteractionSession(
  interactionId: string,
): Promise<InteractionAgentSession | null> {
  type StepOutputLike = {
    session_id?: string
    agent_id?: string
    [k: string]: unknown
  }

  const executions = await getExecutionsByFacet('facet_interaction_id', interactionId)

  for (const execution of executions) {
    for (const step of execution.steps) {
      const output = step.output as StepOutputLike | undefined
      const sessionId = output?.session_id
      const agentId = output?.agent_id
      if (sessionId && agentId) {
        return { sessionId, agentId }
      }
    }
  }

  return null
}

// ── Find Customer (Unified Discovery) ────────────────────────────────────────

/**
 * Unified customer-discovery fan-out.
 *
 * Calls GET /v1/agents/customers/find — the HTTP-exposed twin of the
 * `find_customer` agent tool. Returns a structured view of where the
 * customer exists across Identity, RevenueCat (all configured projects),
 * and Stripe in a single round-trip.
 *
 * At least one of email, aliases, or stripe_customer_id must be supplied.
 * The backend returns 200 with `input_error` set when nothing is provided
 * — callers can surface that message verbatim.
 *
 * Used by the operator-facing Find Customer panel in the inbox inspector.
 * Task 3169.
 */
export async function findCustomer(
  params: FindCustomerParams,
): Promise<FindCustomerResponse> {
  const queryParams: Record<string, string> = {}
  if (params.email) queryParams.email = params.email
  if (params.aliases) queryParams.aliases = params.aliases
  if (params.stripe_customer_id)
    queryParams.stripe_customer_id = params.stripe_customer_id
  return api.get<FindCustomerResponse>(PATHS.findCustomer, queryParams)
}

// ── Batch Fetch for Detail View ───────────────────────────────────────────────

export interface InteractionDetailBatch {
  interaction: Interaction | null
  messages: Message[]
  errors: Record<string, string>
}

/**
 * Fetch interaction detail with thread messages in parallel.
 * Used for inspector Conversation tab.
 */
export async function fetchInteractionDetail(
  id: string
): Promise<InteractionDetailBatch> {
  const errors: Record<string, string> = {}

  // Fetch interaction, thread messages, and full asset metadata in parallel.
  // The thread endpoint returns attachments as bare asset-ID strings, so we
  // also pull /v1/interactions/{id}/assets and enrich the messages with full
  // metadata (filename, content_type, size, url) — mirroring the inbox detail
  // loader so attachment chips render and preview correctly here too.
  const results = await Promise.allSettled([
    getInteraction(id),
    fetchThreadMessages(id),
    getInteractionAssets(id),
  ])

  const getInteractionResult = results[0]
  const getMessagesResult = results[1]
  const getAssetsResult = results[2]

  const interaction =
    getInteractionResult.status === 'fulfilled'
      ? getInteractionResult.value
      : null

  if (getInteractionResult.status === 'rejected') {
    errors.interaction =
      getInteractionResult.reason instanceof Error
        ? getInteractionResult.reason.message
        : String(getInteractionResult.reason)
  }

  const rawMessages: Message[] =
    getMessagesResult.status === 'fulfilled' ? getMessagesResult.value : []

  if (getMessagesResult.status === 'rejected') {
    errors.messages =
      getMessagesResult.reason instanceof Error
        ? getMessagesResult.reason.message
        : String(getMessagesResult.reason)
  }

  // Asset hydration is best-effort: a failure here must not block rendering
  // the message body, so we record the error and fall back to un-enriched
  // attachments (which still render as chips, just without preview/download).
  const rawAssets =
    getAssetsResult.status === 'fulfilled' ? getAssetsResult.value.data : undefined

  if (getAssetsResult.status === 'rejected') {
    errors.assets =
      getAssetsResult.reason instanceof Error
        ? getAssetsResult.reason.message
        : String(getAssetsResult.reason)
  }

  const messages = enrichMessageAttachments(rawMessages, rawAssets)

  return { interaction, messages, errors }
}

/**
 * Fetch all messages in the thread that contains the given interaction.
 * Calls GET /v1/interactions/{id}/thread.
 *
 * The selected interaction is returned as the first item in `data` with
 * `id === interactionId`, allowing the inspector to render the message
 * body of the row the operator clicked.
 *
 * Returns an empty array on failure so the inspector degrades gracefully.
 */
export async function fetchThreadMessages(interactionId: string): Promise<Message[]> {
  try {
    const response = await api.get<{ data: Message[] }>(
      PATHS.interactionThread(interactionId),
    )
    return response.data ?? []
  } catch {
    return []
  }
}
